// Radar View component
// Displays pattern radar chart with bubble visualization and details panel

import { patternsApi, type Pattern, type PatternType, type PatternStatus } from '../services/patterns-api';

interface RadarPattern extends Pattern {
  // Computed positions for the chart
  x: number; // 0-100 (frequency)
  y: number; // 0-100 (performance, inverted for display)
  size: number; // bubble size based on video count
}

export class RadarView {
  private patterns: RadarPattern[] = [];
  private selectedPattern: RadarPattern | null = null;
  private currentTimeRange: '7d' | '30d' | '90d' = '30d';
  private isLoading = false;

  // DOM elements
  private bubblesEl: HTMLElement;
  private detailsEmptyEl: HTMLElement;
  private detailsContentEl: HTMLElement;

  // Type colors
  private typeColors: Record<PatternType, string> = {
    hook: '#49e619',
    script: '#60a5fa',
    visual: '#a78bfa',
    cta: '#fbbf24',
    concept: '#f472b6'
  };

  constructor() {
    this.bubblesEl = document.getElementById('radar-bubbles')!;
    this.detailsEmptyEl = document.getElementById('radar-details-empty')!;
    this.detailsContentEl = document.getElementById('radar-details-content')!;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Time range pills
    document.querySelectorAll('.radar-time-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const range = (pill as HTMLElement).dataset.range as '7d' | '30d' | '90d';
        this.setTimeRange(range);
      });
    });

    // Legend items (filter by type)
    document.querySelectorAll('.radar-legend-item').forEach(item => {
      item.addEventListener('click', () => {
        // Toggle active state
        item.classList.toggle('active');
        this.renderBubbles();
      });
    });

    // Use pattern button
    document.getElementById('radar-use-btn')?.addEventListener('click', () => {
      if (this.selectedPattern) {
        this.usePattern(this.selectedPattern);
      }
    });
  }

  private setTimeRange(range: '7d' | '30d' | '90d'): void {
    this.currentTimeRange = range;

    // Update UI
    document.querySelectorAll('.radar-time-pill').forEach(pill => {
      pill.classList.toggle('active', (pill as HTMLElement).dataset.range === range);
    });

    this.loadPatterns();
  }

  async loadPatterns(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;

    try {
      const result = await patternsApi.listPatterns({ limit: 100 });
      this.patterns = this.computePositions(result.patterns);
      this.renderBubbles();
    } catch (error) {
      console.error('Failed to load patterns for radar:', error);
      this.patterns = [];
      this.renderBubbles();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Compute x/y positions and sizes for each pattern
   * X = frequency (video count relative to max)
   * Y = performance (avgPerformance, inverted for display)
   * Size = based on video count
   */
  private computePositions(patterns: Pattern[]): RadarPattern[] {
    if (patterns.length === 0) return [];

    // Find max values for normalization
    const maxVideoCount = Math.max(...patterns.map(p => p.videoCount || 1));
    const maxPerformance = Math.max(...patterns.map(p => p.avgPerformance || 0.1));

    return patterns.map(p => {
      // X: frequency (0-100), more videos = higher frequency = more right
      const x = Math.min(95, Math.max(5, (p.videoCount / maxVideoCount) * 90 + 5));

      // Y: performance (0-100), higher performance = higher up = lower Y value
      const normalizedPerf = (p.avgPerformance || 0) / maxPerformance;
      const y = Math.min(90, Math.max(10, (1 - normalizedPerf) * 80 + 10));

      // Size: 20-80px based on video count
      const size = Math.min(80, Math.max(24, (p.videoCount / maxVideoCount) * 60 + 24));

      return {
        ...p,
        x,
        y,
        size
      };
    });
  }

  private renderBubbles(): void {
    if (!this.bubblesEl) return;

    // Check which types are active (if none active, show all)
    const activeTypes = new Set<PatternType>();
    document.querySelectorAll('.radar-legend-item.active').forEach(item => {
      activeTypes.add((item as HTMLElement).dataset.type as PatternType);
    });

    const showAll = activeTypes.size === 0;

    // Filter patterns
    const filtered = this.patterns.filter(p => showAll || activeTypes.has(p.type));

    // Render
    this.bubblesEl.innerHTML = filtered.map(pattern => `
      <div
        class="radar-bubble ${this.selectedPattern?.id === pattern.id ? 'selected' : ''}"
        data-id="${pattern.id}"
        data-type="${pattern.type}"
        data-status="${pattern.status}"
        style="
          left: ${pattern.x}%;
          top: ${pattern.y}%;
          width: ${pattern.size}px;
          height: ${pattern.size}px;
          transform: translate(-50%, -50%);
        "
        title="${pattern.name}"
      >
        ${pattern.size > 40 ? `<span class="radar-bubble-label">${this.truncate(pattern.name, 12)}</span>` : ''}
      </div>
    `).join('');

    // Add click listeners
    this.bubblesEl.querySelectorAll('.radar-bubble').forEach(bubble => {
      bubble.addEventListener('click', () => {
        const id = (bubble as HTMLElement).dataset.id;
        const pattern = this.patterns.find(p => p.id === id);
        if (pattern) {
          this.selectPattern(pattern);
        }
      });
    });
  }

  private selectPattern(pattern: RadarPattern): void {
    this.selectedPattern = pattern;

    // Update bubble selection UI
    this.bubblesEl.querySelectorAll('.radar-bubble').forEach(bubble => {
      bubble.classList.toggle('selected', (bubble as HTMLElement).dataset.id === pattern.id);
    });

    // Show details panel
    this.detailsEmptyEl?.classList.add('hidden');
    this.detailsContentEl?.classList.remove('hidden');

    // Populate details
    this.renderDetails(pattern);

    // Record view
    this.recordView(pattern);
  }

  private renderDetails(pattern: RadarPattern): void {
    // Type badge
    const typeEl = document.getElementById('radar-detail-type');
    if (typeEl) {
      typeEl.textContent = pattern.type.toUpperCase();
      typeEl.style.background = `${this.typeColors[pattern.type]}20`;
      typeEl.style.color = this.typeColors[pattern.type];
    }

    // Status badge
    const statusEl = document.getElementById('radar-detail-status');
    if (statusEl) {
      statusEl.className = `radar-status-badge status-${pattern.status}`;
      const icon = this.getStatusIcon(pattern.status);
      statusEl.innerHTML = `
        <span class="material-symbols-outlined">${icon}</span>
        ${this.formatStatus(pattern.status)}
      `;
    }

    // Name & description
    const nameEl = document.getElementById('radar-detail-name');
    if (nameEl) nameEl.textContent = pattern.name;

    const descEl = document.getElementById('radar-detail-description');
    if (descEl) descEl.textContent = pattern.description || 'No description available.';

    // Stats
    const videosEl = document.getElementById('radar-stat-videos');
    if (videosEl) videosEl.textContent = this.formatNumber(pattern.videoCount);

    const perfEl = document.getElementById('radar-stat-perf');
    if (perfEl) {
      const perf = Math.round(pattern.avgPerformance * 100);
      perfEl.textContent = `${perf}%`;
      perfEl.className = `radar-stat-value ${perf >= 50 ? 'radar-stat-positive' : ''}`;
    }

    // Weekly trend (mock data for now)
    const trendEl = document.getElementById('radar-stat-trend');
    if (trendEl) {
      const trend = pattern.status === 'emerging' ? '+12%' : pattern.status === 'declining' ? '-8%' : '+2%';
      const isPositive = trend.startsWith('+');
      trendEl.className = `radar-stat-trend ${isPositive ? '' : 'negative'}`;
      trendEl.innerHTML = `
        <span class="material-symbols-outlined">${isPositive ? 'trending_up' : 'trending_down'}</span>
        <span>${trend}</span>
      `;
    }

    // Sparkline (generate mock data based on status)
    this.renderSparkline(pattern);

    // Examples (placeholder for now)
    this.renderExamples(pattern);

    // Similar patterns
    this.renderSimilarPatterns(pattern);
  }

  private renderSparkline(pattern: RadarPattern): void {
    const sparklineEl = document.getElementById('radar-sparkline');
    if (!sparklineEl) return;

    // Generate mock trend data based on status
    const points = this.generateTrendPoints(pattern.status);
    const pathD = this.pointsToPath(points);
    const areaD = this.pointsToArea(points);

    const svg = sparklineEl.querySelector('svg');
    if (svg) {
      const line = svg.querySelector('.sparkline-line');
      const area = svg.querySelector('.sparkline-area');
      if (line) line.setAttribute('d', pathD);
      if (area) area.setAttribute('d', areaD);
    }
  }

  private generateTrendPoints(status: PatternStatus): number[] {
    // Generate 10 points showing trend direction
    const base = 20;
    const points: number[] = [];

    for (let i = 0; i < 10; i++) {
      let value: number;
      const noise = (Math.random() - 0.5) * 10;

      switch (status) {
        case 'emerging':
          // Upward trend
          value = base + (i * 3) + noise;
          break;
        case 'active':
          // Stable with slight variation
          value = base + 10 + noise;
          break;
        case 'saturated':
          // Peak then slight decline
          value = base + 20 - (i > 5 ? (i - 5) * 2 : 0) + noise;
          break;
        case 'declining':
          // Downward trend
          value = base + 25 - (i * 2) + noise;
          break;
        default:
          value = base + noise;
      }

      points.push(Math.max(5, Math.min(35, value)));
    }

    return points;
  }

  private pointsToPath(points: number[]): string {
    if (points.length === 0) return '';

    const stepX = 100 / (points.length - 1);
    let d = `M0,${40 - points[0]}`;

    for (let i = 1; i < points.length; i++) {
      const x = i * stepX;
      const y = 40 - points[i];
      d += ` L${x},${y}`;
    }

    return d;
  }

  private pointsToArea(points: number[]): string {
    if (points.length === 0) return '';

    const stepX = 100 / (points.length - 1);
    let d = `M0,40 L0,${40 - points[0]}`;

    for (let i = 1; i < points.length; i++) {
      const x = i * stepX;
      const y = 40 - points[i];
      d += ` L${x},${y}`;
    }

    d += ` L100,40 Z`;
    return d;
  }

  private renderExamples(pattern: RadarPattern): void {
    const gridEl = document.getElementById('radar-examples-grid');
    if (!gridEl) return;

    // Placeholder examples (in real app, would fetch from API)
    gridEl.innerHTML = `
      <div class="radar-example">
        <div class="radar-example-thumb" style="background: linear-gradient(135deg, ${this.typeColors[pattern.type]}40, ${this.typeColors[pattern.type]}10);"></div>
        <span class="radar-example-views">1.2M</span>
      </div>
      <div class="radar-example">
        <div class="radar-example-thumb" style="background: linear-gradient(135deg, ${this.typeColors[pattern.type]}30, ${this.typeColors[pattern.type]}10);"></div>
        <span class="radar-example-views">850k</span>
      </div>
      <div class="radar-example">
        <div class="radar-example-thumb" style="background: linear-gradient(135deg, ${this.typeColors[pattern.type]}20, ${this.typeColors[pattern.type]}05);"></div>
        <span class="radar-example-views">620k</span>
      </div>
    `;
  }

  private renderSimilarPatterns(pattern: RadarPattern): void {
    const listEl = document.getElementById('radar-similar-list');
    if (!listEl) return;

    // Find similar patterns (same type, different id)
    const similar = this.patterns
      .filter(p => p.type === pattern.type && p.id !== pattern.id)
      .slice(0, 3);

    if (similar.length === 0) {
      listEl.innerHTML = '<p style="color: var(--text-muted); font-size: 13px;">No similar patterns found</p>';
      return;
    }

    listEl.innerHTML = similar.map(p => `
      <div class="radar-similar-item" data-id="${p.id}">
        <div class="radar-similar-icon">
          <span class="material-symbols-outlined">${this.getTypeIcon(p.type)}</span>
        </div>
        <div class="radar-similar-info">
          <p class="radar-similar-name">${p.name}</p>
          <p class="radar-similar-meta">${this.formatStatus(p.status)} • ${p.videoCount} videos</p>
        </div>
        <span class="radar-similar-change">${p.status === 'emerging' ? '+' : ''}${Math.round(p.avgPerformance * 100)}%</span>
      </div>
    `).join('');

    // Add click listeners
    listEl.querySelectorAll('.radar-similar-item').forEach(item => {
      item.addEventListener('click', () => {
        const id = (item as HTMLElement).dataset.id;
        const p = this.patterns.find(pat => pat.id === id);
        if (p) this.selectPattern(p);
      });
    });
  }

  private async recordView(pattern: Pattern): Promise<void> {
    try {
      await patternsApi.recordSelection({
        patternId: pattern.id,
        patternType: pattern.type,
        action: 'viewed'
      });
    } catch (e) {
      console.error('Failed to record view:', e);
    }
  }

  private async usePattern(pattern: Pattern): Promise<void> {
    try {
      await patternsApi.recordSelection({
        patternId: pattern.id,
        patternType: pattern.type,
        action: 'used'
      });

      // TODO: Open pattern in editor or trigger action
      console.log('Using pattern:', pattern);
      alert(`Pattern "${pattern.name}" marked as used!\n\nNext: integrate with GetPoppy or script generator.`);
    } catch (e) {
      console.error('Failed to mark as used:', e);
    }
  }

  // Utility methods
  private truncate(str: string, len: number): string {
    return str.length > len ? str.substring(0, len) + '…' : str;
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return String(num);
  }

  private formatStatus(status: PatternStatus): string {
    const labels: Record<PatternStatus, string> = {
      emerging: 'Emerging',
      active: 'Stable',
      saturated: 'Saturated',
      declining: 'Declining'
    };
    return labels[status] || status;
  }

  private getStatusIcon(status: PatternStatus): string {
    const icons: Record<PatternStatus, string> = {
      emerging: 'trending_up',
      active: 'check_circle',
      saturated: 'warning',
      declining: 'trending_down'
    };
    return icons[status] || 'help';
  }

  private getTypeIcon(type: PatternType): string {
    const icons: Record<PatternType, string> = {
      hook: 'flash_on',
      script: 'description',
      visual: 'palette',
      cta: 'touch_app',
      concept: 'lightbulb'
    };
    return icons[type] || 'category';
  }
}
